import { z } from 'zod';

/**
 * Cross-cutting primitives reused by resource schemas. Keeping these in one
 * place means field-level rules (coordinate bounds, error shape) are defined
 * once and stay consistent across the API.
 */

/** Standard error envelope returned by the API on any failure. */
export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** Optional structured detail, e.g. flattened Zod validation issues. */
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/** WGS84 longitude, in degrees. */
export const longitudeSchema = z.number().min(-180).max(180);

/** WGS84 latitude, in degrees. Web Mercator can't render beyond ±85. */
export const latitudeSchema = z.number().min(-90).max(90);

/**
 * A point on the map with a human-readable label. This is the app's universal
 * "where am I looking" value — produced by geocoding, by the browser's
 * geolocation API, and by the saved-locations list, and consumed by the map.
 */
export const mapLocationSchema = z.object({
  label: z.string().min(1),
  longitude: longitudeSchema,
  latitude: latitudeSchema,
});
export type MapLocation = z.infer<typeof mapLocationSchema>;

/** `[west, south, east, north]` — the bounds Mapbox returns for a place. */
export const bboxSchema = z.tuple([
  longitudeSchema,
  latitudeSchema,
  longitudeSchema,
  latitudeSchema,
]);
export type BBox = z.infer<typeof bboxSchema>;
